import React from 'react';
import type { PatientProfileTab, PatientProfileTodo } from '../../../patients/types';
import { Icon, type IconKind } from '../../Icon/Icon';

export interface PatientActionBandProps {
  todos: PatientProfileTodo[];
  onOpenTab?: (tab: PatientProfileTab) => void;
}

const ICON: Record<PatientProfileTodo['key'], IconKind> = {
  confirm: 'calendar',
  forms: 'doc',
  insurance: 'shield',
  balance: 'dollar',
  recall: 'refresh',
};

const TONE = {
  warn: { fg: '#9A6B1E', bg: 'var(--gold-tint)' },
  bad: { fg: 'var(--terracotta)', bg: 'var(--terracotta-tint)' },
} as const;

/**
 * "While you have them" — the outstanding items for this patient.
 *
 * The profile is where the front desk lands with the patient on the phone, and
 * until now it was the one surface that couldn't answer "what do I sort out?" —
 * the directory card knew about unconfirmed appointments and outstanding forms;
 * the profile didn't.
 *
 * Each row links to the tab that fixes it rather than acting inline: eligibility
 * belongs to the Insurance tab, forms to the Forms tab. The band renders only
 * when something is outstanding, so its presence is itself the signal.
 */
export function PatientActionBand({ todos, onOpenTab }: PatientActionBandProps) {
  if (!todos.length) return null;

  return (
    <div className="pp-fade mb-[22px]" style={{ animationDelay: '40ms' }}>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
        <div className="flex items-center gap-2 border-b border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-2.5">
          <Icon kind="bolt" size={13} stroke={2} style={{ color: 'var(--gold)' }} />
          <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[var(--ink-3)]">
            While you have them
          </span>
          <span className="font-[var(--font-mono)] text-[10.5px] font-bold text-[var(--ink-4)]">
            {todos.length}
          </span>
        </div>
        <div className="flex flex-col">
          {todos.map((t, i) => {
            const tone = TONE[t.tone];
            const tab = t.tab;
            return (
              <div
                key={t.key}
                className="flex items-center gap-3 px-4 py-[11px]"
                style={{
                  borderBottom: i < todos.length - 1 ? '1px solid var(--cream-2)' : 'none',
                }}
              >
                <span
                  className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[var(--r-md)]"
                  style={{ background: tone.bg, color: tone.fg }}
                >
                  <Icon kind={ICON[t.key]} size={13} stroke={2} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold text-[var(--ink-0)]">
                    {t.label}
                  </span>
                  {t.detail ? (
                    <span className="block truncate text-[12px] text-[var(--ink-3)]">
                      {t.detail}
                    </span>
                  ) : null}
                </span>
                {tab ? (
                  <button
                    type="button"
                    className="shrink-0 whitespace-nowrap border-0 bg-transparent p-0 text-[12.5px] font-semibold text-[var(--ai-3)]"
                    onClick={() => {
                      onOpenTab?.(tab);
                    }}
                  >
                    {tab} →
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
